import ImagemLogo from '../../../../assets/images/logo-lateral.png';
import ImagemLogoGovfacil from '../../../../assets/images/logo-govfacil-white.png';
import ImageWaves from '../../../../assets/images/waves.png';
import { Card, Col, Row } from 'antd';
import {
    TeamOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    TrophyOutlined,
    HeartOutlined,
    AppstoreOutlined,
    RiseOutlined,
} from '@ant-design/icons';
import { ICity } from '../../../../interfaces';
import { urlHttpGov } from '../../../../services/urls';
import { convertMoedaBRDoJSNovo, poeValorComPontosEmMil } from '../../../../utils/Conversores';
import {
    IDashboardFinishedRaffle,
    IDashboardInvoicesByTypeItem,
    IDashboardInvoicesImportHistoryItem,
    IDashboardRegistrationHistoryItem,
    IDashboardSocialInstitutionsData,
    IDashboardTopCompanyItem,
    IDashboardTopUserItem,
    IDashboardUpcomingRaffle,
} from '../../../../services/http/mappers/DashboardMapper';
import RegistrationHistoryChart from '../RegistrationHistoryChart';
import InvoicesImportHistoryChart from '../InvoicesImportHistoryChart';
import InvoicesByTypeChart from '../InvoicesByTypeChart';
import { RegistrationPeriod } from '../../useDashboard';

interface IProps {
    refToPrint: any;
    activeCity: ICity | undefined;
    totalUsers: number;
    totalInvoices: number;
    totalInvoicesValue: number;
    totalPendingInvoices: number;
    invoicesStats: { totalValid: number; totalValue: number };
    topCompanies?: IDashboardTopCompanyItem[];
    topCompaniesAll?: IDashboardTopCompanyItem[];
    topCompaniesNFC?: IDashboardTopCompanyItem[];
    topCompaniesNFS?: IDashboardTopCompanyItem[];
    topCompaniesNFE?: IDashboardTopCompanyItem[];
    topUsers?: IDashboardTopUserItem[];
    socialInstitutionsStats?: IDashboardSocialInstitutionsData;
    hasSocialInstitutionModule?: boolean;
    finishedRaffles?: IDashboardFinishedRaffle[];
    allUpcomingRaffles?: IDashboardUpcomingRaffle[];
    registrationHistory: IDashboardRegistrationHistoryItem[];
    registrationPeriod: RegistrationPeriod;
    invoicesImportHistory: IDashboardInvoicesImportHistoryItem[];
    invoicesByType: IDashboardInvoicesByTypeItem[];
    selectedYear: number;
    allYears: boolean;
    selectedRaffle?: IDashboardUpcomingRaffle | IDashboardFinishedRaffle;
    latestPendingRaffles?: IDashboardUpcomingRaffle[];
}

export default function ReportDashboard({
    refToPrint,
    activeCity,
    totalUsers,
    totalInvoices,
    totalInvoicesValue,
    invoicesStats,
    finishedRaffles = [],
    socialInstitutionsStats,
    hasSocialInstitutionModule = false,
    registrationHistory,
    registrationPeriod,
    invoicesImportHistory,
    invoicesByType,
    selectedYear,
    allYears,
    selectedRaffle,
}: IProps) {
    const filterLabel = (() => {
        if (selectedRaffle) {
            const raffleDate = selectedRaffle.dateOfRaffle
                ? ` (${new Date(selectedRaffle.dateOfRaffle).toLocaleDateString('pt-BR')})`
                : '';
            return `Sorteio: ${selectedRaffle.description}${raffleDate}`;
        }
        if (!allYears && selectedYear) {
            return `Ano: ${selectedYear}`;
        }
        return 'Todos os anos';
    })();

    const finishedRafflesList = finishedRaffles ?? [];
    const totalPrizesDistributed = finishedRafflesList.reduce((acc, r) => acc + (r.totalPrizeValue || 0), 0);
    const totalTicketsFinished = finishedRafflesList.reduce((acc, r) => acc + (r.totalTickets || 0), 0);

    const socialData = socialInstitutionsStats;
    const hasSocial = hasSocialInstitutionModule && socialData && (socialData.institutions?.length > 0 || (socialData.total || 0) > 0);
    const totalLinked = socialData?.totalLinkedUsers || 0;
    const totalUnlinked = Math.max(0, (totalUsers || 0) - totalLinked);

    const validationRate = totalInvoices > 0
        ? ((invoicesStats.totalValid / totalInvoices) * 100).toFixed(1).replace('.', ',') + '%'
        : '0%';

    const kpiSummaryItems = [
        {
            label: 'Total de usuários',
            value: poeValorComPontosEmMil(totalUsers),
            icon: <TeamOutlined />,
            color: '#1677ff',
            bgColor: '#eff6ff',
        },
        {
            label: 'Notas válidas',
            value: poeValorComPontosEmMil(invoicesStats.totalValid),
            icon: <CheckCircleOutlined />,
            color: '#15803d',
            bgColor: '#f0fdf4',
        },
        {
            label: 'Total importado',
            value: poeValorComPontosEmMil(totalInvoices),
            icon: <FileTextOutlined />,
            color: '#2563eb',
            bgColor: '#eff6ff',
        },
        {
            label: 'Taxa de Validação de Notas',
            value: validationRate,
            icon: <RiseOutlined />,
            color: '#0284c7',
            bgColor: '#f0f9ff',
        },
        {
            label: 'Valor das válidas',
            value: `R$ ${convertMoedaBRDoJSNovo(invoicesStats.totalValue)}`,
            icon: <DollarOutlined />,
            color: '#15803d',
            bgColor: '#f0fdf4',
        },
        {
            label: 'Valor total importado',
            value: `R$ ${convertMoedaBRDoJSNovo(totalInvoicesValue)}`,
            icon: <DollarOutlined />,
            color: '#2563eb',
            bgColor: '#eff6ff',
        },
    ];

    return (
        <div ref={refToPrint} className="report-content">
            <div className="header-report" style={{ display: 'none' }}>
                <div className="header-info">
                    <h1>Relatório do Dashboard</h1>
                    <p className="header-city">{activeCity?.city ? `${activeCity.city} - ${activeCity.uf}` : 'Todos os municípios'}</p>
                    <p className="header-filter">
                        <span className="filter-label">Filtros aplicados:</span>{' '}
                        <span className="filter-value">{filterLabel}</span>
                    </p>
                </div>
                <div className="header-logo">
                    <img
                        src={activeCity?.logo_county ? `${urlHttpGov}/logo_county/${activeCity.logo_county}` : ImagemLogo}
                        alt="Logo município"
                    />
                </div>
            </div>
            <div className="footer-report" style={{ display: 'none' }}>
                <img
                    width="180px"
                    src={ImagemLogo}
                    alt="Logo do Sistema"
                    className="logo-img footer-report-logo-img"
                />
                <div className="footer-report-date-generated">
                    <p>
                        Arquivo gerado em {new Date().toLocaleDateString('pt-BR')} às{' '}
                        {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                </div>
                <img
                    width="200px"
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        marginBottom: '50px',
                        marginRight: '15mm',
                        padding: '15px',
                    }}
                    src={ImagemLogoGovfacil}
                    alt="Logo Govfácil"
                    className="logo-img"
                />
                <img className="footer-report-img" alt="Imagem rodapé" src={ImageWaves} width="100%" />
            </div>
            <div className="page page-first">
                <div className="line-divider" />
                <Card
                    className="section-report report-card"
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1e293b' }}>
                            <AppstoreOutlined style={{ color: '#1677ff' }} />
                            <span>Resumo Geral</span>
                        </div>
                    }
                    style={{ borderRadius: 6, padding: 0, marginBottom: 16, boxShadow: '0 3px 8px rgba(0,0,0,0.08)' }}
                    bodyStyle={{ padding: 14 }}
                >
                    <Row gutter={[10, 10]} style={{ alignItems: 'stretch' }}>
                        {kpiSummaryItems.map((item, idx) => (
                            <Col span={8} key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                <div
                                    className="info-item"
                                    style={{
                                        background: '#ffffff',
                                        borderRadius: 4,
                                        border: '1px solid #e2e8f0',
                                        borderLeft: `3px solid ${item.color}`,
                                        padding: '10px 14px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                        height: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            {item.label}
                                        </span>
                                        <div
                                            style={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: 4,
                                                background: item.bgColor,
                                                color: item.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 13,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {item.icon}
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            fontSize: item.value.length > 15 ? '1.15rem' : '1.25rem',
                                            fontWeight: 800,
                                            color: item.color,
                                            lineHeight: 1.2,
                                            letterSpacing: '-0.02em',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {item.value}
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card>

                <div className="section-report chart-report-wrapper">
                    <RegistrationHistoryChart
                        data={registrationHistory}
                        isLoading={false}
                        period={registrationPeriod}
                        onPeriodChange={() => { }}
                        showLabels
                        isRaffleFiltered={Boolean(selectedRaffle)}
                    />
                </div>
            </div>
            <div className="page">
                <div className="section-report chart-report-wrapper">
                    <InvoicesImportHistoryChart
                        data={invoicesImportHistory}
                        isLoading={false}
                        showTable={true}
                        showStatsCards={true}
                        showLabels={true}
                        chartHeight={210}
                    />
                </div>
            </div>
            <div className="page">
                <div className="section-report chart-report-wrapper">
                    <InvoicesByTypeChart data={invoicesByType} isLoading={false} compact />
                </div>

                {finishedRafflesList.length > 0 && (
                    <Card
                        className="section-report report-card"
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1e293b' }}>
                                <TrophyOutlined style={{ color: '#1677ff' }} />
                                <span>Resumo de Sorteios Realizados e Premiações</span>
                            </div>
                        }
                        style={{ borderRadius: 6, padding: 0, marginTop: 14, marginBottom: 14, boxShadow: '0 3px 8px rgba(0,0,0,0.08)' }}
                        bodyStyle={{ padding: 14 }}
                    >
                        <Row gutter={[10, 10]} style={{ alignItems: 'stretch' }}>
                            <Col span={8}>
                                <div style={{ background: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #15803d', padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Total em Prêmios
                                        </span>
                                        <div style={{ width: 26, height: 26, borderRadius: 4, background: '#f0fdf4', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                                            <DollarOutlined />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#15803d', lineHeight: 1.2, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                                        R$ {convertMoedaBRDoJSNovo(totalPrizesDistributed)}
                                    </div>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div style={{ background: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #1677ff', padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Bilhetes Gerados
                                        </span>
                                        <div style={{ width: 26, height: 26, borderRadius: 4, background: '#eff6ff', color: '#1677ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                                            <FileTextOutlined />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1677ff', lineHeight: 1.2, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                                        {poeValorComPontosEmMil(totalTicketsFinished)}
                                    </div>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div style={{ background: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #2563eb', padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Sorteios Concluídos
                                        </span>
                                        <div style={{ width: 26, height: 26, borderRadius: 4, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                                            <TrophyOutlined />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#2563eb', lineHeight: 1.2, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                                        {finishedRafflesList.length}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                )}

                {hasSocial && (
                    <Card
                        className="section-report report-card"
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1e293b' }}>
                                <HeartOutlined style={{ color: '#0284c7' }} />
                                <span>Resumo de Entidades Sociais</span>
                            </div>
                        }
                        style={{ borderRadius: 6, padding: 0, marginTop: 14, marginBottom: 14, boxShadow: '0 3px 8px rgba(0,0,0,0.08)' }}
                        bodyStyle={{ padding: 14 }}
                    >
                        <Row gutter={[10, 10]} style={{ alignItems: 'stretch' }}>
                            <Col span={8}>
                                <div style={{ background: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #15803d', padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Entidades Cadastradas
                                        </span>
                                        <div style={{ width: 26, height: 26, borderRadius: 4, background: '#f0fdf4', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                                            <HeartOutlined />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#15803d', lineHeight: 1.2, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                                        {poeValorComPontosEmMil(socialData?.total || 0)}
                                    </div>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div style={{ background: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #0284c7', padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Munícipes Apoiadores
                                        </span>
                                        <div style={{ width: 26, height: 26, borderRadius: 4, background: '#f0f9ff', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                                            <TeamOutlined />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0284c7', lineHeight: 1.2, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                                        {poeValorComPontosEmMil(totalLinked)}
                                    </div>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div style={{ background: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #d97706', padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Sem Vínculo Escolhido
                                        </span>
                                        <div style={{ width: 26, height: 26, borderRadius: 4, background: '#fffbeb', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                                            <ClockCircleOutlined />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#d97706', lineHeight: 1.2, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                                        {poeValorComPontosEmMil(totalUnlinked)}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                )}
            </div>

        </div>
    );
}
